import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Observable } from 'rxjs';
import {
  BfmDownloadButtonComponent,
  BfmDownloadContract,
  DOWNLOAD_BUTTON_CONFIG,
} from '@celerofinancas/common-domain';
import {
  CdsPaginationModule,
  CDS_PAGINATION_RENDERER,
  CDS_PAGINATION_TOKEN,
} from '@celerofinancas/design-system';
import { TRACKING_MODELS } from '@celerofinancas/service-utils';
import { FeatureFlagService } from '@celerofinancas/ui-feature-flag';
import { DownloadFilters } from '@celerofinancas/ui-http-services';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import { DataTableConfig } from '@shared/components/data-table/data-table.interface';
import { CreditCardPaginationRendererComponent } from '@shared/components/credit-card-pagination-renderer/credit-card-pagination-renderer.component';
import { CreditCardsHttpDataSourceService } from '../credit-cards-data-source.service';
import {
  CreditCardTransactionRow,
  CreditCardTransactionsQuery,
  TRANSACTIONS_PAGE_SIZE_OPTIONS,
} from '../credit-cards-data-interface';

/**
 * Export the download button asks for. The selected bill is the only filter:
 * the screen offers none, and the export is of the whole bill, not of the page
 * on screen
 */
export const CREDIT_CARD_DOWNLOAD_CONTRACT: BfmDownloadContract = {
  domain: 'credit_card',
  exportType: 'credit_card_bill',
  fileName: 'Lancamentos_cartao',
  origin: 'credit_card',
  clickEvent: TRACKING_MODELS.downloadCreditCardClick,
};

/**
 * Feature flag the download button is behind.
 */
export const CREDIT_CARD_DOWNLOAD_FLAG = 'front_credit_cards@download';

/**
 * Server-side paginated table of a bill's transactions ("Lançamentos"). Owns
 * the fetch + pagination state for the given `billId`; the cds-pagination view
 * (skinned by CreditCardPaginationRendererComponent) is fed by inputs only.
 */
@Component({
  selector: 'caixa-webcomponent-transactions-table',
  standalone: true,
  templateUrl: './transactions-table.component.html',
  styleUrl: './transactions-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, DataTableComponent, CdsPaginationModule, BfmDownloadButtonComponent],
  providers: [
    // Seeds cds-pagination's offset control with our page sizes (lib default is
    // [15, 30, 50, 100][0] = 15) and swaps its UI for the Figma footer.
    { provide: CDS_PAGINATION_TOKEN, useValue: { offsetOptions: TRANSACTIONS_PAGE_SIZE_OPTIONS } },
    { provide: CDS_PAGINATION_RENDERER, useValue: CreditCardPaginationRendererComponent },
    {
      provide: DOWNLOAD_BUTTON_CONFIG,
      useValue: {
        icon: 'cds-icon-download',
        styleClass: 's-hollow c-primary',
        anchorCard: true,
      },
    },
  ],
})
export class TransactionsTableComponent implements OnChanges {
  /**
   * Bill whose transactions are listed. Changing it reloads from the first page.
   */
  @Input()
  public billId = '';

  /**
   * Whether the current page is being fetched.
   */
  public readonly isLoading = signal(false);

  /**
   * Transactions for the current page.
   */
  public readonly transactions = signal<CreditCardTransactionRow[]>([]);

  /**
   * Total transactions across all pages.
   */
  public readonly totalItems = signal(0);

  /**
   * Current page (1-based).
   */
  public readonly page = signal(1);

  /**
   * Page size (items per page).
   */
  public readonly pageSize = signal(TRANSACTIONS_PAGE_SIZE_OPTIONS[0]);

  /**
   * Total number of pages. Derived from the total item count and the current
   * page size so it updates synchronously when the page size changes — instead
   * of lagging behind the next fetch (which left the footer showing a stale
   * "Página 1 de N" computed against the previous page size).
   */
  public readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalItems() / this.pageSize())),
  );

  /**
   * Error state for the request.
   */
  public readonly error = signal(false);

  /**
   * Monotonic id of the latest request. Used to ignore responses from
   * superseded requests (rapid bill switching / paging).
   */
  private requestId = 0;

  /**
   * Page size options offered by the paginator.
   */
  protected readonly pageSizeOptions = TRANSACTIONS_PAGE_SIZE_OPTIONS;

  /**
   * Static column configuration. "Lançamento"/"Tipo de transação" are sortable
   * only to show the caret (the endpoint has no sort param, so sortChange is
   * intentionally not bound).
   */
  protected readonly tableConfig: DataTableConfig = {
    columns: [
      { key: 'lancamento', label: 'Lançamento', sortable: true },
      { key: 'parcela', label: 'Parcela', align: 'center' },
      { key: 'tipo', label: 'Tipo de transação', sortable: true },
      { key: 'categoria', label: 'Categoria' },
      { key: 'realizadoEm', label: 'Realizado em' },
      { key: 'valor', label: 'Valor', align: 'right' },
    ],
    emptyMessage: 'Nenhum lançamento encontrado',
    rowStyle: 'plain',
  };

  /**
   * Credit cards data source.
   */
  private readonly dataSource = inject(CreditCardsHttpDataSourceService);

  /**
   * Destroy ref for subscription teardown.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Feature flag service.
   */
  private readonly featureFlag: FeatureFlagService<string> = inject(FeatureFlagService);

  /**
   * Controls whether the download button should be visible.
   */
  protected readonly isDownloadEnabled$: Observable<boolean> =
    this.featureFlag.isFlagEnabled$(CREDIT_CARD_DOWNLOAD_FLAG);

  /**
   * Which export the download button asks for.
   */
  protected readonly downloadContract = CREDIT_CARD_DOWNLOAD_CONTRACT;

  /**
   * Whether there is anything to export. An empty listing has no file to ask
   * for, so the button isn't offered at all.
   */
  public readonly canDownload = computed(() => this.totalItems() > 0);

  /**
   * Filter sent in the export request. The bill is the whole scope of the file:
   * the account and the reference month are the backend's to resolve from it,
   * and the pagination on screen doesn't restrict the export.
   */
  public get downloadFilters(): DownloadFilters {
    return { bill_id: this.billId };
  }

  /**
   * Reloads from the first page whenever the bill changes.
   *
   * @param changes input changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if ('billId' in changes) {
      this.page.set(1);
      this.load();
    }
  }

  /**
   * Handles paginator page change.
   *
   * @param page target page (1-based)
   */
  public onPageChange(page: number): void {
    this.page.set(Math.max(1, Number(page)));
    this.load();
  }

  /**
   * Handles paginator page size change. Resets to the first page.
   *
   * @param pageSize new page size
   */
  public onOffsetChange(pageSize: number): void {
    this.pageSize.set(Number(pageSize));
    this.page.set(1);
    this.load();
  }

  /**
   * Fetches the current page of transactions for the selected bill.
   */
  private load(): void {
    if (!this.billId) {
      this.transactions.set([]);
      this.totalItems.set(0);
      this.error.set(false);
      return;
    }

    const requestId = ++this.requestId;
    this.isLoading.set(true);
    this.error.set(false);

    const query: CreditCardTransactionsQuery = {
      billId: this.billId,
      page: this.page(),
      pageSize: this.pageSize(),
    };

    this.dataSource
      .listTransactions(query)
      .pipe(
        finalize(() => {
          if (requestId === this.requestId) {
            this.isLoading.set(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          if (requestId !== this.requestId) {
            return;
          }
          this.transactions.set(result.items);
          this.totalItems.set(result.pagination.totalItems);
        },
        error: () => {
          if (requestId !== this.requestId) {
            return;
          }
          this.transactions.set([]);
          this.totalItems.set(0);
          this.error.set(true);
        },
      });
  }
}
